import React from 'react';
import { FiMoreHorizontal, FiTrendingUp } from 'react-icons/fi';
import { HiOutlineShieldCheck, HiOutlineDocumentReport, HiOutlineExclamationCircle, HiOutlineFolder, HiOutlineClock, HiOutlineClipboardCheck } from 'react-icons/hi';
import DateRangePicker from '../../../components/DateRangePicker';
import { useComplianceCentre } from '../context/ComplianceCentreContext';

export default function ComplianceCentreOverview() {

  const { data, loading, error, range, setRange, refresh, setPanel } = useComplianceCentre();
  const summary = data?.summary;
  const value = key => loading ? '…' : error ? '—' : summary?.[key] ?? '—';
  const overall = summary?.overall;
  const change = summary?.change;
  
  
  // Array configuration holding metric data configurations
  const metrics = [
    {
      id: 'overall',
      title: 'Overall Compliance',
      value: overall == null ? value('overall') : overall + '%',
      label: loading ? 'Loading' : error ? 'Unavailable' : overall == null ? 'No records' : overall >= 90 ? 'Good' : 'Needs attention',
      labelColor: 'text-emerald-600',
      subtext: change == null ? 'No comparison data' : `${change >= 0 ? '+' : ''}${change}% vs previous period`,
      subtextColor: 'text-emerald-600',
      hasTrend: change !== null && change !== undefined,
      bgIcon: 'bg-emerald-50 text-emerald-600',
      icon: HiOutlineShieldCheck
    },
    {
      id: 'areas',
      title: 'Areas Compliant',
      value: loading || error ? value('totalAreas') : `${summary?.compliantAreas ?? 0} / ${summary?.totalAreas ?? 0}`,
      subtext: !loading && !error && summary?.totalAreas ? Math.round(summary.compliantAreas * 100 / summary.totalAreas) + '%' : '—',
      subtextColor: 'text-gray-500',
      bgIcon: 'bg-blue-50 text-blue-600',
      icon: HiOutlineDocumentReport
    },
    {
      id: 'actions',
      title: 'Open Actions',
      value: value('openActions'),
      subtext: loading || error ? '—' : `High equipment risk: ${summary?.highPriority ?? 0}`,
      subtextColor: 'text-red-600 font-bold',
      bgIcon: 'bg-orange-50 text-orange-600',
      icon: HiOutlineExclamationCircle
    },
    {
      id: 'docs',
      title: 'Documents',
      value: value('documents'),
      subtext: 'Saved evidence files',
      subtextColor: 'text-blue-600',
      bgIcon: 'bg-indigo-50 text-indigo-600',
      icon: HiOutlineFolder
    },
    {
      id: 'expiring',
      title: 'Expiring Soon',
      value: value('expiring'),
      subtext: 'Next 30 days',
      subtextColor: 'text-gray-400',
      bgIcon: 'bg-red-50 text-red-500',
      icon: HiOutlineClock
    },
    {
      id: 'audit',
      title: 'Last Internal Audit',
      value: 'Not tracked',
      subtext: 'Audit records not available',
      subtextColor: 'text-gray-500',
      bgIcon: 'bg-blue-50 text-blue-600',
      icon: HiOutlineClipboardCheck
    }
  ];

  return (
    <div className="w-full space-y-6 font-sans select-none">
      
      {/* 1. Main Header Title block */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="space-y-1 text-left">
          <h1 className="text-xl md:text-2xl font-black text-slate-900 tracking-tight">
            Compliance Centre
          </h1>
          <p className="text-xs md:text-sm text-slate-500 font-medium">
            Your central hub for compliance, records, and regulatory requirements.
          </p>
        </div>

        {/* Right side controls block */}
        <div className="flex items-center space-x-3 self-start md:self-auto flex-wrap gap-y-2">
          <DateRangePicker range={range} setRange={setRange} label="Select compliance date range" title="Check dates, incident occurrence dates and equipment registration dates within the selected range" />


          {/* More options triple dot wrapper */}
          <button type="button" onClick={refresh} aria-label="Refresh compliance data" title="Refresh compliance data" className="p-3 border border-gray-200 rounded-xl bg-white hover:bg-gray-50 text-gray-400 transition shadow-sm h-[46px] w-[46px] flex items-center justify-center">
            <FiMoreHorizontal className="w-5 h-5" />
          </button>
        </div>
      </div>

      {error && <p role="alert" className="rounded-xl border border-red-100 bg-red-50 p-3 text-xs font-semibold text-red-600">{error}</p>}
      {/* 2. Grid Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {metrics.map((card) => {
          const CardIcon = card.icon;

          return (
            <div
              key={card.id}
              title={card.id === 'overall' ? 'Approved passing results divided by required or recorded checks for the selected range; missing daily checks count as pending.' : card.id === 'actions' ? 'Open incidents and overdue equipment schedules from records in the selected range. Incident priority and due dates are not tracked; equipment risk is used when recorded.' : card.id === 'expiring' ? 'Equipment contracts and warranties expiring within 30 days after the range end date' : undefined}
              onClick={() => { if (card.id === 'docs') setPanel('documents'); else if (card.id === 'actions') setPanel('actions'); else if (card.id === 'expiring') setPanel('expiring'); }}
              className="bg-white border border-gray-100/80 rounded-2xl p-4 shadow-sm flex items-start space-x-3 min-w-0 transition hover:shadow-md hover:border-gray-200/50"
            >
              {/* Icon */}
              <div
                className={`w-10 h-10 ${card.bgIcon} rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm`}
              >
                <CardIcon className="w-5 h-5 stroke-[2]" />
              </div>

              {/* Content */}
              <div className="space-y-1 min-w-0 text-left flex-1">
                <span className="block text-[10px] font-black text-gray-400 uppercase tracking-wider truncate">
                  {card.title}
                </span>

                {/* Main value */}
                <div className="flex items-baseline space-x-1.5 flex-wrap">
                  <span className="text-xl font-black text-slate-900 tracking-tight leading-tight">
                    {card.value}
                  </span>

                  {card.label && (
                    <span className={`text-xs font-black ${card.labelColor}`}>
                      {card.label}
                    </span>
                  )}
                </div>

                {/* Subtext */}
                <div className="flex items-center space-x-1 text-[10px] font-semibold leading-tight">
                  {card.hasTrend && (
                    <FiTrendingUp className="w-3 h-3 text-emerald-600 flex-shrink-0" />
                  )}

                  <span className={card.subtextColor}>
                    {card.subtext}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
